import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import config from "../config";

const AiAssistantContext = createContext(null);

export const AiAssistantProvider = ({ children }) => {
  const [query, setQuery] = useState("");
  const [activePrompt, setActivePrompt] = useState("");
  const [response, setResponse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const activeAbortControllerRef = useRef(null);

  const sendAiQuery = useCallback(async (customPrompt) => {
    const text = (customPrompt || query).trim();
    if (!text || isLoading) return;

    setActivePrompt(text);
    setQuery("");
    setIsLoading(true);
    setResponse(null);

    // Abort previous in-flight request if any
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    const controller = new AbortController();
    activeAbortControllerRef.current = controller;

    try {
      const token = localStorage.getItem("rovionToken");
      const res = await fetch(`${config.API_URL}/api/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          messages: [{ role: "user", content: text }],
        }),
        signal: controller.signal,
      });

      const data = await res.json();
      if (data.success) {
        setResponse(data.response);
      } else {
        throw new Error(data.message || "Yapay zeka yanıt veremedi.");
      }
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("[AI Assistant] İstek kullanıcı tarafından iptal edildi.");
        return;
      }
      setResponse(
        "Bağlantı Hatası: Yapay zeka motoruna ulaşılamadı. LM Studio servisinin açık ve backend'in aktif olduğundan emin olun."
      );
    } finally {
      setIsLoading(false);
    }
  }, [query, isLoading]);

  const copyToClipboard = useCallback(() => {
    if (!response) return;
    navigator.clipboard.writeText(response);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  }, [response]);

  const clearAiResponse = useCallback(() => {
    if (activeAbortControllerRef.current) {
      activeAbortControllerRef.current.abort();
    }
    setResponse(null);
    setActivePrompt("");
    setQuery("");
    setIsLoading(false);
  }, []);

  return (
    <AiAssistantContext.Provider
      value={{
        query,
        setQuery,
        activePrompt,
        setActivePrompt,
        response,
        setResponse,
        isLoading,
        isCopied,
        sendAiQuery,
        copyToClipboard,
        clearAiResponse,
      }}
    >
      {children}
    </AiAssistantContext.Provider>
  );
};

export const useAiAssistant = () => {
  const context = useContext(AiAssistantContext);
  if (!context) {
    throw new Error("useAiAssistant must be used within an AiAssistantProvider");
  }
  return context;
};

export default AiAssistantContext;
